import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  AutorenewOutlined,
  CheckCircleOutlined,
  ExpandMoreOutlined,
  Groups2Outlined,
  MonetizationOnOutlined,
  PendingActionsOutlined,
  PriorityHighOutlined,
  ShieldOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import { GET_DASHBOARD_SNAPSHOT } from "graphql/dashboard";
import {
  formatCount,
  formatCurrency,
  formatFocusAreaLabel,
  formatPercent,
  formatRelativeDateTime,
  formatStatusLabel,
} from "utils/formatters";
import { getGraphQLErrorMessage } from "utils/graphqlError";

const dashboardYear = String(new Date().getUTCFullYear());

const resolveWelcomeName = () => {
  const fullName = localStorage.getItem("fullName");
  const username = localStorage.getItem("username");
  const email = localStorage.getItem("email");

  const baseValue = fullName || username || email || "";
  const trimmedValue = String(baseValue).trim();

  if (!trimmedValue) return "";

  if (trimmedValue.includes("@")) {
    return trimmedValue.split("@")[0];
  }

  return trimmedValue.split(/\s+/)[0];
};

const getToneStyles = (theme, tone = "default") => {
  if (tone === "success") {
    return {
      accent: theme.palette.success?.main || "#2e7d32",
      soft: theme.palette.success?.light || "rgba(46, 125, 50, 0.12)",
    };
  }

  if (tone === "warning") {
    return {
      accent: theme.palette.warning?.main || "#ed6c02",
      soft: theme.palette.warning?.light || "rgba(237, 108, 2, 0.12)",
    };
  }

  if (tone === "error") {
    return {
      accent: theme.palette.error?.main || "#d32f2f",
      soft: theme.palette.error?.light || "rgba(211, 47, 47, 0.12)",
    };
  }

  return {
    accent: theme.palette.secondary.main,
    soft: theme.palette.secondary[100],
  };
};

const DashboardCard = ({ children, sx = {}, emphasis = "default", interactive = false, ...props }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const emphasisStyles =
    emphasis === "highlight"
      ? {
          borderColor: `${theme.palette.secondary.main}40`,
          background: `linear-gradient(145deg, ${theme.palette.background.alt} 0%, ${theme.palette.secondary[100]}1A 100%)`,
        }
      : {
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)",
          background: `linear-gradient(165deg, ${theme.palette.background.alt} 0%, ${theme.palette.background.alt} 65%, ${theme.palette.secondary[100]}14 100%)`,
        };

  return (
    <Box
      borderRadius="1rem"
      p="1.25rem"
      border="1px solid"
      boxShadow={isDark ? "0 14px 30px rgba(0,0,0,0.28)" : "0 10px 24px rgba(15, 23, 42, 0.08)"}
      transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
      {...props}
      sx={{
        ...emphasisStyles,
        ...(interactive
          ? {
              cursor: "default",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: isDark
                  ? "0 18px 36px rgba(0,0,0,0.35)"
                  : "0 16px 32px rgba(15, 23, 42, 0.14)",
                borderColor: `${theme.palette.secondary.main}55`,
              },
            }
          : null),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const SectionHeading = ({ title, subtitle, action }) => {
  const theme = useTheme();

  return (
    <FlexBetween mb="1rem" gap="1rem" flexWrap="wrap">
      <Box>
        <Typography variant="h4" fontWeight={700} color={theme.palette.secondary[100]}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography mt="0.2rem" color={theme.palette.secondary[300]}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </FlexBetween>
  );
};

const MetricCard = ({
  title,
  value,
  subtitle,
  tone = "default",
  icon,
  progress,
  chip,
  highlight = false,
}) => {
  const theme = useTheme();
  const toneStyles = getToneStyles(theme, tone);

  return (
    <DashboardCard emphasis={highlight ? "highlight" : "default"} interactive>
      <FlexBetween alignItems="flex-start" gap="0.75rem">
        <Box>
          <Typography color={theme.palette.secondary[300]} variant="h6">
            {title}
          </Typography>
          <Typography
            mt="0.55rem"
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.02em"
            color={theme.palette.secondary[100]}
            lineHeight={1.15}
          >
            {value ?? "—"}
          </Typography>
        </Box>
        <Box
          width={42}
          height={42}
          borderRadius="50%"
          display="grid"
          placeItems="center"
          sx={{ backgroundColor: toneStyles.soft, color: toneStyles.accent, flexShrink: 0, border: `1px solid ${toneStyles.accent}33` }}
        >
          {icon}
        </Box>
      </FlexBetween>

      {chip ? <Box mt="0.9rem">{chip}</Box> : null}

      {typeof progress === "number" ? (
        <Box mt="1rem">
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(progress, 100))}
            sx={{
              height: 8,
              borderRadius: 999,
              backgroundColor: theme.palette.primary.light,
              "& .MuiLinearProgress-bar": {
                backgroundColor: toneStyles.accent,
                borderRadius: 999,
              },
            }}
          />
        </Box>
      ) : null}

      <Typography mt="0.9rem" color={theme.palette.secondary[300]} minHeight={44} lineHeight={1.55}>
        {subtitle || "No additional context available."}
      </Typography>
    </DashboardCard>
  );
};

const EmptyStateCard = ({ message }) => {
  const theme = useTheme();

  return (
    <DashboardCard>
      <Typography color={theme.palette.secondary[300]}>{message}</Typography>
    </DashboardCard>
  );
};

const LoadingDashboard = () => (
  <Box mt="20px" display="grid" gap="20px">
    <Skeleton variant="rounded" height={180} />
    <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="20px">
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} gridColumn={{ xs: "span 12", sm: "span 6", xl: "span 3" }}>
          <Skeleton variant="rounded" height={150} />
        </Box>
      ))}
    </Box>
    <Skeleton variant="rounded" height={120} />
    <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="20px">
      {Array.from({ length: 6 }).map((_, index) => (
        <Box key={index} gridColumn={{ xs: "span 12", md: "span 6", xl: "span 3" }}>
          <Skeleton variant="rounded" height={170} />
        </Box>
      ))}
    </Box>
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery("(min-width: 1200px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, loading, error, refetch } = useQuery(GET_DASHBOARD_SNAPSHOT, {
    variables: { year: dashboardYear },
    fetchPolicy: "cache-and-network",
  });

  const snapshot = data?.dashboardSnapshot;
  const welcomeName = useMemo(() => resolveWelcomeName(), []);
  const groupSnapshot = snapshot?.groupSnapshot;
  const memberSnapshot = snapshot?.memberSnapshot;

  const contributionTone = memberSnapshot?.isContributionUpToDate ? "success" : "warning";
  const groupContributionTone = groupSnapshot?.isContributionUpToDate ? "success" : "warning";
  const annualCollectionTone = Number(groupSnapshot?.collectionRatePercent) >= 80 ? "success" : "warning";
  const riskTone = Number(groupSnapshot?.overdueContributionAmount) > 0 || Number(groupSnapshot?.overdueLoanAmount) > 0
    ? "error"
    : "success";

  const personalContributionProgressToDate = Number(memberSnapshot?.expectedContributionToDate)
    ? (Number(memberSnapshot?.contributionPaidToDate) / Number(memberSnapshot?.expectedContributionToDate)) * 100
    : memberSnapshot?.isContributionUpToDate
      ? 100
      : 0;

  const primaryMetrics = useMemo(
    () => [
      {
        title: "Total contributions so far",
        value: formatCurrency(groupSnapshot?.collectedContributionSoFar),
        subtitle: `Group total for ${groupSnapshot?.year || dashboardYear}`,
        tone: annualCollectionTone,
        icon: <TrendingUpOutlined fontSize="small" />,
      },
      {
        title: "Total loans given out",
        value: formatCurrency(groupSnapshot?.totalLoanPrincipalDisbursedThisYear),
        subtitle: `Loan book outstanding: ${formatCurrency(groupSnapshot?.totalLoanBookOutstanding)}`,
        tone: "default",
        icon: <ArrowUpwardOutlined fontSize="small" />,
      },
      {
        title: "Total loans paid back",
        value: formatCurrency(groupSnapshot?.totalLoansPaidBack),
        subtitle: `Group pending repayment: ${formatCurrency(groupSnapshot?.totalLoanBookOutstanding)}`,
        tone: Number(groupSnapshot?.totalLoanBookOutstanding) > 0 ? "warning" : "success",
        icon: <AutorenewOutlined fontSize="small" />,
      },
      {
        title: "Current cash balance",
        value: formatCurrency(groupSnapshot?.currentCashBalance ?? 0),
        subtitle: "Cash currently available to the chamaa",
        tone: Number(groupSnapshot?.currentCashBalance ?? 0) > 0 ? "success" : "default",
        icon: <AccountBalanceWalletOutlined fontSize="small" />,
        highlight: true,
      },
      {
        title: "My available balance",
        value: formatCurrency(memberSnapshot?.availableContributionAfterLiabilities),
        subtitle: "After your current liabilities",
        tone: Number(memberSnapshot?.availableContributionAfterLiabilities) > 0 ? "success" : "warning",
        icon: <MonetizationOnOutlined fontSize="small" />,
        highlight: true,
      },
      {
        title: "Chairman's Kitty",
        value: formatCurrency(groupSnapshot?.totalFinesPaid),
        subtitle: "Total fines paid by the group",
        tone: Number(groupSnapshot?.totalFinesPaid) > 0 ? "success" : "default",
        icon: <MonetizationOnOutlined fontSize="small" />,
      },
    ],
    [annualCollectionTone, groupSnapshot, memberSnapshot]
  );

  const quickStats = useMemo(
    () => [
      {
        title: "Active members",
        value: formatCount(groupSnapshot?.activeMembersCount),
        subtitle: `${formatCount(groupSnapshot?.membersFullyUpToDateCount)} members are fully current this month`,
        tone: "default",
        icon: <Groups2Outlined fontSize="small" />,
      },
      {
        title: "Contribution status to date",
        value: formatStatusLabel(groupSnapshot?.isContributionUpToDate),
        subtitle: `${formatCurrency(groupSnapshot?.collectedContributionToDate)} collected against ${formatCurrency(groupSnapshot?.expectedContributionToDate)} due to date`,
        tone: groupContributionTone,
        icon: <TrendingUpOutlined fontSize="small" />,
        progress: Number(groupSnapshot?.collectionRateToDatePercent) || 0,
        highlight: true,
      },
      {
        title: "Overdue exposure",
        value: formatCurrency(
          Number(groupSnapshot?.overdueContributionAmount || 0) +
            Number(groupSnapshot?.overdueLoanAmount || 0)
        ),
        subtitle: "Combined overdue contributions and loans",
        tone: riskTone,
        icon: <WarningAmberOutlined fontSize="small" />,
        highlight: true,
      },
      {
        title: "My contribution status",
        value: formatStatusLabel(memberSnapshot?.isContributionUpToDate),
        subtitle: `${formatCurrency(memberSnapshot?.contributionPaidToDate)} paid against ${formatCurrency(memberSnapshot?.expectedContributionToDate)} due to date`,
        tone: contributionTone,
        icon: <CheckCircleOutlined fontSize="small" />,
        chip: (
          <Chip
            size="small"
            label={formatStatusLabel(memberSnapshot?.isContributionUpToDate)}
            color={memberSnapshot?.isContributionUpToDate ? "success" : "warning"}
            variant="outlined"
          />
        ),
        highlight: true,
      },
    ],
    [contributionTone, groupContributionTone, groupSnapshot, memberSnapshot, riskTone]
  );

  const groupMetrics = useMemo(
    () => [
      {
        title: "Members up to date",
        value: formatCount(groupSnapshot?.membersFullyUpToDateCount),
        subtitle: `${formatCount(groupSnapshot?.membersWithSchedulesCount)} members have active schedules`,
        tone: "success",
        icon: <CheckCircleOutlined fontSize="small" />,
      },
      {
        title: "Members behind",
        value: formatCount(groupSnapshot?.membersBehindOnContributionsCount),
        subtitle: "Members needing contribution follow-up",
        tone: Number(groupSnapshot?.membersBehindOnContributionsCount) > 0 ? "warning" : "success",
        icon: <PriorityHighOutlined fontSize="small" />,
      },
      {
        title: "Collected to date",
        value: formatCurrency(groupSnapshot?.collectedContributionToDate),
        subtitle: `Expected to date: ${formatCurrency(groupSnapshot?.expectedContributionToDate)}`,
        tone: groupContributionTone,
        icon: <MonetizationOnOutlined fontSize="small" />,
      },
      {
        title: "Outstanding to date",
        value: formatCurrency(groupSnapshot?.contributionOutstandingToDate),
        subtitle: `Pending contribution: ${formatCurrency(groupSnapshot?.pendingContributionAmount)}`,
        tone: Number(groupSnapshot?.contributionOutstandingToDate) > 0 ? "error" : "success",
        icon: <ArrowDownwardOutlined fontSize="small" />,
      },
      {
        title: "Annual contribution progress",
        value: formatPercent(groupSnapshot?.collectionRatePercent),
        subtitle: `Annual target: ${formatCurrency(groupSnapshot?.expectedContributionForYear)}`,
        tone: annualCollectionTone,
        icon: <TrendingUpOutlined fontSize="small" />,
      },
      {
        title: "Current cash balance",
        value: formatCurrency(groupSnapshot?.currentCashBalance ?? 0),
        subtitle: "Current cash position available to the chamaa",
        tone: Number(groupSnapshot?.currentCashBalance ?? 0) > 0 ? "success" : "default",
        icon: <AccountBalanceWalletOutlined fontSize="small" />,
      },
      {
        title: "Active loans",
        value: formatCount(groupSnapshot?.activeLoansCount),
        subtitle: `${formatCount(groupSnapshot?.loanApplicationsInPipelineCount)} application(s) in pipeline`,
        tone: "default",
        icon: <TrendingUpOutlined fontSize="small" />,
      },
      {
        title: "Loan book outstanding",
        value: formatCurrency(groupSnapshot?.totalLoanBookOutstanding),
        subtitle: `Paid back so far: ${formatCurrency(groupSnapshot?.totalLoansPaidBack)}`,
        tone: "default",
        icon: <ArrowUpwardOutlined fontSize="small" />,
      },
      {
        title: "Amount due this month",
        value: formatCurrency(groupSnapshot?.amountDueThisMonth),
        subtitle: `Overdue loans: ${formatCurrency(groupSnapshot?.overdueLoanAmount)}`,
        tone: Number(groupSnapshot?.overdueLoanAmount) > 0 ? "error" : "success",
        icon: <AutorenewOutlined fontSize="small" />,
      },
      {
        title: "Portfolio at risk",
        value: formatPercent(groupSnapshot?.portfolioAtRiskPercent),
        subtitle: "Share of the loan book currently under stress",
        tone: Number(groupSnapshot?.portfolioAtRiskPercent) > 0 ? "warning" : "success",
        icon: <ShieldOutlined fontSize="small" />,
      },
      {
        title: "Active guarantors",
        value: formatCount(groupSnapshot?.activeGuarantorsCount),
        subtitle: `Guarantee exposure: ${formatCurrency(groupSnapshot?.totalGuaranteeExposure)}`,
        tone: "default",
        icon: <ShieldOutlined fontSize="small" />,
      },
      {
        title: "Pending transactions",
        value: formatCount(groupSnapshot?.pendingTransactionsCount),
        subtitle: `Pending amount: ${formatCurrency(groupSnapshot?.pendingTransactionsAmount)}`,
        tone: Number(groupSnapshot?.pendingTransactionsCount) > 0 ? "warning" : "success",
        icon: <PendingActionsOutlined fontSize="small" />,
      },
    ],
    [annualCollectionTone, groupContributionTone, groupSnapshot]
  );

  const personalMetrics = useMemo(
    () => [
      {
        title: "Contribution paid to date",
        value: formatCurrency(memberSnapshot?.contributionPaidToDate),
        subtitle: `Expected to date: ${formatCurrency(memberSnapshot?.expectedContributionToDate)}`,
        tone: contributionTone,
        icon: <MonetizationOnOutlined fontSize="small" />,
      },
      {
        title: "Contribution status",
        value: formatStatusLabel(memberSnapshot?.isContributionUpToDate),
        subtitle: memberSnapshot?.isContributionUpToDate
          ? "You are current on monthly contribution dues based on the schedule due date."
          : `Outstanding to date: ${formatCurrency(memberSnapshot?.contributionOutstandingToDate)}`,
        tone: contributionTone,
        icon: <CheckCircleOutlined fontSize="small" />,
        chip: (
          <Chip
            size="small"
            label={formatStatusLabel(memberSnapshot?.isContributionUpToDate)}
            color={memberSnapshot?.isContributionUpToDate ? "success" : "warning"}
            variant="filled"
          />
        ),
        highlight: true,
      },
      {
        title: "My annual contribution total",
        value: formatCurrency(memberSnapshot?.totalContributionSoFar),
        subtitle: `Annual goal for ${dashboardYear}: ${formatCurrency(memberSnapshot?.expectedTotalContributionForYear)}`,
        tone: "default",
        icon: <TrendingUpOutlined fontSize="small" />,
      },
      {
        title: "Loans taken",
        value: formatCount(memberSnapshot?.loansTakenCount),
        subtitle: `Principal taken: ${formatCurrency(memberSnapshot?.totalLoanPrincipalTaken)}`,
        tone: "default",
        icon: <ArrowUpwardOutlined fontSize="small" />,
      },
      {
        title: "My loans settled / pending",
        value: `${formatCurrency(memberSnapshot?.totalLoansPaid)} / ${formatCurrency(memberSnapshot?.totalLoansPending)}`,
        subtitle: "Paid versus still pending",
        tone: Number(memberSnapshot?.totalLoansPending) > 0 ? "warning" : "success",
        icon: <AutorenewOutlined fontSize="small" />,
      },
      {
        title: "Loans guaranteed",
        value: formatCount(memberSnapshot?.loansGuaranteedCount),
        subtitle: `Guarantorship amount: ${formatCurrency(memberSnapshot?.totalGuarantorshipAmount)}`,
        tone: Number(memberSnapshot?.loansGuaranteedCount) > 0 ? "default" : "success",
        icon: <ShieldOutlined fontSize="small" />,
      },
      {
        title: "Debt exposure",
        value: formatCurrency(
          Number(memberSnapshot?.totalLoansPending || 0) + Number(memberSnapshot?.totalGuarantorshipAmount || 0)
        ),
        subtitle: `Pending loans + guarantorship (${formatCurrency(memberSnapshot?.totalGuarantorshipAmount)})`,
        tone: Number(memberSnapshot?.totalLoansPending || 0) > 0 ? "warning" : "success",
        icon: <ShieldOutlined fontSize="small" />,
      },
    ],
    [contributionTone, memberSnapshot]
  );

  return (
    <Box m={{ xs: "1rem", sm: "1.25rem 1.5rem", md: "1.5rem 2.5rem" }} sx={{ overflowX: "hidden" }}>
      <FlexBetween gap="1rem" flexWrap="wrap">
        <Header
          title="DASHBOARD"
          subtitle={`Your chamaa health and personal position for ${dashboardYear}`}
        />

        <Button
          variant="outlined"
          onClick={() => refetch()}
          sx={{
            borderColor: theme.palette.secondary.main,
            color: theme.palette.secondary.main,
            px: 2,
            py: 1,
          }}
        >
          Refresh snapshot
        </Button>
      </FlexBetween>

      {loading && !snapshot ? (
        <LoadingDashboard />
      ) : error ? (
        <DashboardCard mt="20px">
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {getGraphQLErrorMessage(error)}
          </Alert>
        </DashboardCard>
      ) : (
        <Box mt="20px" display="grid" gap="20px">
          <DashboardCard>
            <Stack spacing={2}>
              <FlexBetween gap="1rem" flexWrap="wrap" alignItems="flex-start">
                <Box maxWidth={isLargeScreen ? "70%" : "100%"}>
                  <Typography variant="h4" fontWeight={700} color={theme.palette.secondary[100]}>
                    {welcomeName ? `Hi ${welcomeName}! Welcome back` : "Welcome back"}
                  </Typography>
                  <Typography mt="0.75rem" variant="h6" color={theme.palette.secondary[300]}>
                    {snapshot?.experienceSummary || "Your latest chamaa summary will appear here once data is available."}
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <Chip
                    label={formatFocusAreaLabel(snapshot?.recommendedFocusArea)}
                    color="secondary"
                    variant="filled"
                  />
                  <Chip
                    label={formatRelativeDateTime(snapshot?.generatedAtUtc)}
                    variant="outlined"
                  />
                </Stack>
              </FlexBetween>

              <Divider />

              <Box sx={{ p: { xs: "0.85rem", md: "1rem" }, borderRadius: "0.9rem", backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)", border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)" }}>
                <SectionHeading
                  title="Key numbers at a glance"
                  subtitle="Top financial metrics first — everything else comes after."
                />
                <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="16px">
                  {primaryMetrics.map((metric) => (
                    <Box
                      key={metric.title}
                      gridColumn={{ xs: "span 12", sm: "span 6", lg: "span 4" }}
                      minWidth={0}
                    >
                      <MetricCard {...metric} />
                    </Box>
                  ))}
                </Box>
              </Box>

              <Divider />

              <Box sx={{ p: { xs: "0.85rem", md: "1rem" }, borderRadius: "0.9rem", backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)", border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)" }}>
              <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="16px">
                {quickStats.map((metric) => (
                  <Box
                    key={metric.title}
                    gridColumn={{ xs: "span 12", sm: "span 6", xl: "span 3" }}
                  >
                    <MetricCard {...metric} />
                  </Box>
                ))}
              </Box>
              </Box>
            </Stack>
          </DashboardCard>

          <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="20px">
            <Box gridColumn={{ xs: "span 12", xl: "span 8" }}>
              <DashboardCard sx={{ height: "100%" }}>
                <SectionHeading
                  title="Chamaa Health"
                  subtitle={`Group summary for ${groupSnapshot?.year || dashboardYear}`}
                  action={
                    groupSnapshot ? (
                      <Chip
                        size={isMobile ? "small" : "medium"}
                        label={`To-date collection ${formatPercent(groupSnapshot?.collectionRateToDatePercent)}`}
                        color={groupContributionTone === "success" ? "success" : "warning"}
                        variant="outlined"
                      />
                    ) : null
                  }
                />

                {groupSnapshot ? (
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography color={theme.palette.secondary[300]}>
                        {groupSnapshot.healthSummary || "No group health summary available yet."}
                      </Typography>
                    </Box>

                    <Alert severity={groupSnapshot.isContributionUpToDate ? "success" : "warning"} icon={<PriorityHighOutlined fontSize="inherit" />}>
                      {groupSnapshot.isContributionUpToDate
                        ? "All members with schedules are current on the contribution amounts due up to this month, so the chamaa is on green."
                        : groupSnapshot.priorityAction || "Some members are behind on the contribution amounts due up to this month, so the chamaa needs follow-up."}
                    </Alert>

                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap="16px">
                      <Box>
                        <FlexBetween mb="0.45rem">
                          <Typography color={theme.palette.secondary[300]}>
                            Contribution collection to date
                          </Typography>
                          <Typography fontWeight={700} color={theme.palette.secondary[100]}>
                            {formatPercent(groupSnapshot.collectionRateToDatePercent)}
                          </Typography>
                        </FlexBetween>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(Number(groupSnapshot.collectionRateToDatePercent) || 0, 100))}
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            backgroundColor: theme.palette.primary.light,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                groupContributionTone === "success"
                                  ? theme.palette.success?.main || "#2e7d32"
                                  : theme.palette.warning?.main || "#ed6c02",
                              borderRadius: 999,
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <FlexBetween mb="0.45rem">
                          <Typography color={theme.palette.secondary[300]}>
                            Annual contribution collection
                          </Typography>
                          <Typography fontWeight={700} color={theme.palette.secondary[100]}>
                            {formatPercent(groupSnapshot.collectionRatePercent)}
                          </Typography>
                        </FlexBetween>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(Number(groupSnapshot.collectionRatePercent) || 0, 100))}
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            backgroundColor: theme.palette.primary.light,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                annualCollectionTone === "success"
                                  ? theme.palette.success?.main || "#2e7d32"
                                  : theme.palette.warning?.main || "#ed6c02",
                              borderRadius: 999,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Accordion disableGutters sx={{ backgroundColor: "transparent", boxShadow: "none", border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)", borderRadius: "0.85rem", overflow: "hidden", "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 2, minHeight: 54, "& .MuiAccordionSummary-content": { my: 1 } }}>
                        <Typography fontWeight={600} color={theme.palette.secondary[100]}>
                          More group insights
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pb: 2 }}>
                        <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="16px">
                          {groupMetrics.map((metric) => (
                            <Box
                              key={metric.title}
                              gridColumn={{ xs: "span 12", sm: "span 6", xl: "span 4" }}
                            >
                              <MetricCard {...metric} />
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                ) : (
                  <EmptyStateCard message="Group health data is not available for this member yet." />
                )}
              </DashboardCard>
            </Box>

            <Box gridColumn={{ xs: "span 12", xl: "span 4" }}>
              <DashboardCard sx={{ height: "100%" }}>
                <SectionHeading
                  title="My Position"
                  subtitle={`Member snapshot for ${memberSnapshot?.year || dashboardYear}`}
                  action={
                    memberSnapshot ? (
                      <Chip
                        label={formatStatusLabel(memberSnapshot?.isContributionUpToDate)}
                        color={memberSnapshot?.isContributionUpToDate ? "success" : "warning"}
                        variant="filled"
                      />
                    ) : null
                  }
                />

                {memberSnapshot ? (
                  <Stack spacing={2}>
                    <Alert severity={memberSnapshot.isContributionUpToDate ? "success" : "warning"}>
                      {memberSnapshot.isContributionUpToDate
                        ? "You are current on the contribution amount due up to this month based on your schedule due date."
                        : `You still have ${formatCurrency(memberSnapshot.contributionOutstandingToDate)} outstanding to become current for this month.`}
                    </Alert>

                    <Box>
                      <FlexBetween mb="0.45rem">
                        <Typography color={theme.palette.secondary[300]}>
                          Personal contribution progress to date
                        </Typography>
                        <Typography fontWeight={700} color={theme.palette.secondary[100]}>
                          {formatPercent(personalContributionProgressToDate)}
                        </Typography>
                      </FlexBetween>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(personalContributionProgressToDate, 100))}
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: theme.palette.primary.light,
                          "& .MuiLinearProgress-bar": {
                            backgroundColor:
                              contributionTone === "success"
                                ? theme.palette.success?.main || "#2e7d32"
                                : theme.palette.warning?.main || "#ed6c02",
                            borderRadius: 999,
                          },
                        }}
                      />
                    </Box>

                    <Accordion disableGutters sx={{ backgroundColor: "transparent", boxShadow: "none", border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)", borderRadius: "0.85rem", overflow: "hidden", "&:before": { display: "none" } }}>
                      <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 2, minHeight: 54, "& .MuiAccordionSummary-content": { my: 1 } }}>
                        <Typography fontWeight={600} color={theme.palette.secondary[100]}>
                          More personal insights
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pb: 2 }}>
                        <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="16px">
                          {personalMetrics.map((metric) => (
                            <Box key={metric.title} gridColumn="span 12">
                              <MetricCard {...metric} />
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                ) : (
                  <EmptyStateCard message="Your personal dashboard snapshot is not available yet." />
                )}
              </DashboardCard>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
